import { useEffect, useMemo, useState } from "react";
import {
  Calculator,
  Wallet,
  Home,
  Check,
  ArrowLeft,
  Send,
  Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AppShell from "@/components/layout/AppShell";
import { useNavigate } from "react-router-dom";
import { settingsApi } from "@/lib/crmApi";
import SendSimulationDialog from "@/components/crm/SendSimulationDialog";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";

/**
 * Simulateur de paiement pour un appartement.
 *
 * Le mode "Achat Comptant" utilise un échéancier fixe en 6 étapes alignées
 * sur l'avancement du chantier (réservation → remise des clés). Les montants
 * sont calculés à partir du prix de vente unique saisi par l'utilisateur ou
 * pré-rempli depuis un programme.
 *
 * Palette : tout le tableau utilise des dégradés VERT pour rester cohérent
 * avec le mode comptant — l'orange historique du badge "20%" / la barre
 * orange de total ont été remplacés par du vert SOGI.
 */

// Default catalogue + comptant schedule — used while the API request is in
// flight, AND as a fallback when the backend hasn't been seeded yet (or when
// the admin emptied the catalogue). Mirrors backend DEFAULT_SIM_APARTMENTS.
const DEFAULT_APARTMENTS = [
  { id: "f2-a", typo: "F2", unite: "F2-A", surface: 43,  prix:  40_000_000, programme: "RYAD Almadies" },
  { id: "f2-b", typo: "F2", unite: "F2-B", surface: 45,  prix:  42_000_000, programme: "RYAD Almadies" },
  { id: "f3-a", typo: "F3", unite: "F3-A", surface: 65,  prix:  60_000_000, programme: "RYAD Almadies" },
];

const DEFAULT_SCHEDULE_COMPTANT = [
  { etape: "Réservation", offset_months: 0,  pct: 5  },
  { etape: "Démarrage",   offset_months: 1,  pct: 30 },
  { etape: "Étage 2",     offset_months: 5,  pct: 30 },
  { etape: "Étage 5",     offset_months: 9,  pct: 20 },
  { etape: "Finition",    offset_months: 13, pct: 10 },
  { etape: "Remise des Clés", offset_months: 17, pct: 5 },
];

const labelOf = (a) => `${a.typo} · ${a.unite} · ${a.surface} m²`;

const fmtFCFA = (n) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(
    Math.round(n)
  ) + " FCFA";

const MOIS_FR = [
  "Janv", "Fév", "Mars", "Avr", "Mai", "Juin",
  "Juil", "Août", "Sept", "Oct", "Nov", "Déc",
];
const fmtMonthYear = (d) => `${MOIS_FR[d.getMonth()]} ${d.getFullYear()}`;

export default function SimulationPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // v1.8.1 — pull the active project's NAME so we can pre-select the
  // simulator catalogue chip for it. The simulation_apartments are tagged
  // by `programme` name; matching against `activeProject.nom` gives us
  // strict isolation while still letting admins click "Tous" if they
  // want a cross-project view.
  const { activeProject } = useSettings();
  // Controls the "Envoyer au lead" dialog opened from the action bar.
  const [sendOpen, setSendOpen] = useState(false);
  // Catalogue + payment modes pulled from /api/settings so admins can edit
  // them without touching the code. Falls back to the local defaults during
  // load and when the catalogue is empty (e.g. fresh install).
  const [apartments, setApartments] = useState(DEFAULT_APARTMENTS);
  const [modes, setModes] = useState([
    { key: "comptant", label: "Achat Comptant", enabled: true, rate_pct: 0,
      schedule: DEFAULT_SCHEDULE_COMPTANT },
    { key: "location_vente", label: "Location-Vente", enabled: false, rate_pct: 8.5,
      schedule: [] },
  ]);
  // v1.8.1 — default chip = active project's name so the catalogue
  // ships pre-filtered when the user arrives on the page. Commercials
  // who never see "Tous" anyway will only see their project's units.
  // Admins can still click "Tous" or any other chip to broaden the view.
  const [programmeFilter, setProgrammeFilter] = useState(
    activeProject?.nom || "Tous"
  );

  // Track project changes after first mount so toggling the topbar
  // project selector immediately reflects in the simulator catalogue.
  useEffect(() => {
    setProgrammeFilter(activeProject?.nom || "Tous");
  }, [activeProject?.id, activeProject?.nom]);

  // Respect the active project (`current_projet` from /me) so each commercial
  // sees the simulation catalogue that's been customised for the project they
  // currently work on. Admins viewing "all projects" (no current_projet) get
  // the global defaults — sensible because there's no single project context.
  const activeProjetId = user?.current_projet || null;

  useEffect(() => {
    let cancelled = false;
    const fetcher = activeProjetId
      ? settingsApi
          .getForProject(activeProjetId)
          .then((r) => r?.effective || {})
      : settingsApi.get();
    fetcher
      .then((s) => {
        if (cancelled) return;
        if (Array.isArray(s.simulation_apartments) && s.simulation_apartments.length) {
          setApartments(s.simulation_apartments);
        }
        if (Array.isArray(s.simulation_modes) && s.simulation_modes.length) {
          setModes(s.simulation_modes);
        }
      })
      .catch(() => { /* keep defaults */ });
    return () => { cancelled = true; };
  }, [activeProjetId]);

  // Build the list of programmes for the filter chip row.
  // v1.8.1 — "Tous" is admin-only. Commercials only ever see chips of
  // programmes they're allowed in (which, for the simulator, are the
  // ones present in the apartments list — already filtered server-side
  // by getForProject() for that user's active project).
  const isAdmin = user?.role === "admin";
  const programmes = useMemo(() => {
    const set = new Set();
    apartments.forEach((a) => a.programme && set.add(a.programme));
    return isAdmin
      ? ["Tous", ...[...set].sort()]
      : [...set].sort();
  }, [apartments, isAdmin]);

  const filteredApartments = useMemo(
    () =>
      programmeFilter === "Tous"
        ? apartments
        : apartments.filter((a) => a.programme === programmeFilter),
    [apartments, programmeFilter]
  );

  const enabledModes = useMemo(
    () => modes.filter((m) => m.enabled !== false || m.key === "comptant"),
    [modes]
  );

  // Apartment selection state — declared FIRST so hooks below can reference
  // it safely (otherwise the dependency array hits the TDZ on `apartId`).
  const [apartId, setApartId] = useState(null);
  const [apartLabel, setApartLabel] = useState("");
  const [surface, setSurface] = useState(0);
  const [prix, setPrix] = useState(0);

  const selectedApart = apartments.find((a) => a.id === apartId) || null;

  const pickApart = (id) => {
    const a = apartments.find((x) => x.id === id);
    if (!a) return;
    setApartId(id);
    setApartLabel(labelOf(a));
    setSurface(a.surface);
    setPrix(a.prix);
  };

  // First-load: pick the first apartment as soon as the catalogue lands.
  useEffect(() => {
    if (!apartId && apartments.length > 0) {
      const a = apartments[0];
      setApartId(a.id);
      setApartLabel(labelOf(a));
      setSurface(a.surface);
      setPrix(a.prix);
    }
  }, [apartments, apartId]);

  // Mode state.
  const [modeKey, setModeKey] = useState("comptant");
  const selectedMode = modes.find((m) => m.key === modeKey) || modes[0];

  // When the user switches apartment, auto-switch the active mode away from
  // anything the new apartment forbids (e.g. F2 → no Location-Vente).
  useEffect(() => {
    const current = apartments.find((a) => a.id === apartId);
    if (!current) return;
    const blocked = current.disabled_modes || [];
    if (blocked.includes(modeKey)) {
      const fallback = modes.find(
        (m) => !blocked.includes(m.key) && m.enabled !== false
      );
      if (fallback) setModeKey(fallback.key);
    }
  }, [apartId, apartments, modes, modeKey]);

  // For monthly modes (location-vente), the user picks a duration in years.
  // The schedule is generated dynamically below.
  const [durationYears, setDurationYears] = useState(null);
  useEffect(() => {
    // When the user switches to a monthly mode, default to its first
    // available duration so the table never lands on "no duration picked".
    if (selectedMode?.kind === "monthly") {
      const opts = selectedMode.durations_years || [];
      if (!opts.includes(durationYears)) {
        setDurationYears(opts[0] || null);
      }
    } else {
      setDurationYears(null);
    }
  }, [selectedMode, durationYears]);

  // Down-payment override: the lead can pay MORE than the configured minimum
  // (typical when they have cash on hand and want to lower the monthly).
  // We never let the value drop below the configured `downpayment_pct`
  // (typically 30% for Location-Vente — politique SOGI).
  //
  // The user can express the apport as either a PERCENTAGE or a FIXED AMOUNT
  // in FCFA — `apportMode` toggles between the two. Whichever the user typed
  // last is the source of truth; the other is computed for display.
  const minDownpayment = Number(selectedMode?.downpayment_pct || 0);
  const minDownpaymentAmount = Math.round((prix * minDownpayment) / 100);
  const [apportMode, setApportMode] = useState("percent"); // "percent" | "amount"
  const [customDownpayment, setCustomDownpayment] = useState(minDownpayment);
  const [customDownpaymentAmount, setCustomDownpaymentAmount] = useState(
    minDownpaymentAmount
  );
  useEffect(() => {
    // Reset to the configured minimum whenever the mode (or apartment price)
    // changes — avoids carrying a stale 50% from a previous mode/apartment.
    setCustomDownpayment(minDownpayment);
    setCustomDownpaymentAmount(Math.round((prix * minDownpayment) / 100));
    setApportMode("percent");
  }, [selectedMode?.key, minDownpayment, prix]);

  // Effective apport % actually used in the calculations (clamped to [min, 100]).
  // When the user is editing in "amount" mode, derive % from the amount.
  const rawDownpaymentPct =
    apportMode === "amount"
      ? prix > 0
        ? ((Number(customDownpaymentAmount) || 0) * 100) / prix
        : 0
      : Number(customDownpayment) || 0;
  const effectiveDownpayment = Math.min(
    100,
    Math.max(minDownpayment, rawDownpaymentPct)
  );
  const effectiveDownpaymentAmount = (prix * effectiveDownpayment) / 100;
  const [startDate] = useState(() => {
    // Réservation = aujourd'hui ; les autres étapes sont calculées en offset
    const d = new Date();
    d.setDate(1); // ancrage sur le 1er du mois pour des dates propres
    return d;
  });

  // Build the rows for the active mode. Two paths:
  //
  //  A) kind="schedule" (Comptant): use the saved milestones directly. The
  //     mode's `rate_pct` (if any) inflates the total, then each milestone's
  //     pct is applied to that grossed-up amount.
  //
  //  B) kind="monthly" (Location-Vente): generate a schedule on the fly =
  //     downpayment + N monthly payments where N = duration_years × 12. The
  //     monthly amount uses the standard annuity formula at the annual rate
  //     `rate_pct`. With rate=0 it degrades cleanly to equal instalments.
  const rows = useMemo(() => {
    if (!selectedMode) return [];
    const rate = Number(selectedMode.rate_pct || 0);

    if (selectedMode.kind === "monthly") {
      const years = Number(durationYears || selectedMode.durations_years?.[0] || 0);
      const months = years * 12;
      // Use the effective downpayment (custom override clamped to the
      // configured minimum) instead of the static config value.
      const dpct = effectiveDownpayment;
      if (months === 0) return [];

      const downpayment = (prix * dpct) / 100;
      const principal = prix - downpayment;
      const i = rate / 100 / 12;
      // Annuity formula. With i=0 fall back to flat instalments.
      const monthly =
        i === 0
          ? principal / months
          : (principal * i) / (1 - Math.pow(1 + i, -months));

      const out = [];
      // Step 0: downpayment ("Réservation") if non-zero
      if (downpayment > 0) {
        out.push({
          etape: "Réservation (apport)",
          pct: dpct,
          offset_months: 0,
          date: "—",
          montant: downpayment,
        });
      }
      // Then one row per month — collapse into a single summary row when
      // the schedule is too long to render comfortably (>24 lines).
      const totalDue = downpayment + monthly * months;
      const monthlyPct = (monthly / totalDue) * 100;
      if (months <= 24) {
        for (let k = 1; k <= months; k++) {
          const d = new Date(startDate);
          d.setMonth(d.getMonth() + k);
          out.push({
            etape: `Mensualité ${k}/${months}`,
            pct: monthlyPct,
            offset_months: k,
            date: fmtMonthYear(d),
            montant: monthly,
          });
        }
      } else {
        // Long schedules: collapse into a summary row with a "× N" hint.
        const firstD = new Date(startDate);
        firstD.setMonth(firstD.getMonth() + 1);
        const lastD = new Date(startDate);
        lastD.setMonth(lastD.getMonth() + months);
        out.push({
          etape: `${months} mensualités égales`,
          pct: 100 - dpct,
          offset_months: 1,
          date: `${fmtMonthYear(firstD)} → ${fmtMonthYear(lastD)}`,
          montant: monthly * months,
          isMonthlySummary: true,
          monthly,
        });
      }
      return out;
    }

    // Path A: schedule mode (Comptant)
    const sched = selectedMode.schedule || [];
    const totalWithRate = prix * (1 + rate / 100);
    return sched.map((step) => {
      const off = Number(step.offset_months ?? step.offsetMonths ?? 0);
      const dateStr =
        off === 0
          ? "—"
          : (() => {
              const d = new Date(startDate);
              d.setMonth(d.getMonth() + off);
              return fmtMonthYear(d);
            })();
      const pct = Number(step.pct || 0);
      return {
        etape: step.etape,
        pct,
        offset_months: off,
        date: dateStr,
        montant: (totalWithRate * pct) / 100,
      };
    });
  }, [prix, startDate, selectedMode, durationYears, effectiveDownpayment]);

  const totalPct = rows.reduce((s, r) => s + r.pct, 0);
  const totalMontant = rows.reduce((s, r) => s + r.montant, 0);

  // Financing breakdown — only meaningful for monthly modes (Location-Vente).
  // Shows the user EXACTLY where the money goes:
  //   apport + capital emprunté → mensualité × N → coût total + intérêts
  const financing = useMemo(() => {
    if (!selectedMode || selectedMode.kind !== "monthly") return null;
    const years = Number(durationYears || selectedMode.durations_years?.[0] || 0);
    const months = years * 12;
    if (months === 0) return null;
    const rate = Number(selectedMode.rate_pct || 0);
    const apport = (prix * effectiveDownpayment) / 100;
    const capital = Math.max(0, prix - apport);
    const i = rate / 100 / 12;
    const monthly =
      i === 0
        ? capital / months
        : (capital * i) / (1 - Math.pow(1 + i, -months));
    const totalMensualites = monthly * months;
    const coutTotal = apport + totalMensualites;
    const interets = Math.max(0, coutTotal - prix);
    return {
      months,
      years,
      rate,
      apport,
      capital,
      monthly,
      totalMensualites,
      coutTotal,
      interets,
    };
  }, [selectedMode, durationYears, prix, effectiveDownpayment]);

  return (
    <AppShell topbarProps={{ showSearch: false, showAdd: false }}>
      <div className="mx-auto max-w-[900px] px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/")}
          className="mb-4 gap-1.5"
          data-testid="simu-back"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </Button>

        {/* Apartment header — editorial real-estate style.
            - Solid navy with paper-grain texture for tactile depth.
            - Vertical gold accent rule (4px) anchors the left edge — a
              motif used throughout the rest of the surface to tie things
              together visually.
            - Tiny "RYAD ALMADIES — 2026" eyebrow line in spaced uppercase
              gives the marketing-collateral feel.
            - Display serif (Fraunces) for the price — much more memorable
              than the previous Work Sans bold number. */}
        <div className="relative overflow-hidden rounded-2xl border border-white/5 bg-[hsl(var(--accent-navy))] bg-paper-grain text-white shadow-elevated">
          {/* Vertical gold accent rule — 4px wide, full height */}
          <div className="absolute inset-y-0 left-0 w-1 bg-[hsl(var(--accent-gold))]" />
          {/* Top-right diagonal flourish — subtle gold glow that catches the
              eye without competing with the price */}
          <div
            aria-hidden
            className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full opacity-25"
            style={{
              background:
                "radial-gradient(circle at center, hsl(var(--accent-gold) / 0.55) 0%, transparent 65%)",
            }}
          />

          <div className="relative flex flex-col gap-4 p-5 sm:flex-row sm:items-end sm:justify-between sm:p-7">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]/90">
                {selectedApart?.programme || "Catalogue SOGI"}
                {" · "}
                <span className="text-white/50">Réf. {(apartId || "—").slice(0, 6).toUpperCase()}</span>
              </p>
              <h1 className="font-display mt-2 text-3xl font-medium leading-[1.1] tracking-tight sm:text-4xl">
                {selectedApart?.typo} <span className="text-white/40">·</span>{" "}
                <span className="text-[hsl(var(--accent-gold))]">{selectedApart?.unite || "—"}</span>
              </h1>
              <p className="mt-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-white/60">
                <span className="inline-block h-px w-6 bg-white/30" />
                {surface} m² habitables
              </p>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-[10px] uppercase tracking-[0.22em] text-white/50">
                Prix de vente
              </p>
              <p className="font-display mt-1.5 text-4xl font-medium tabular-nums text-[hsl(var(--accent-gold))] sm:text-5xl">
                {fmtFCFA(prix)}
              </p>
            </div>
          </div>
        </div>

        {/* 1) Catalogue des appartements à vendre — clic = pré-remplissage */}
        <section className="mt-6">
          <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
            <p className="text-label text-muted-foreground">
              1 · Choisir un appartement
              <span className="ml-2 text-[10px] normal-case tracking-normal text-muted-foreground/70">
                ({filteredApartments.length} appartement{filteredApartments.length > 1 ? "s" : ""})
              </span>
            </p>
            {/* Programme filter row — only shown when at least one programme is set */}
            {programmes.length > 1 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <Building2 className="h-3 w-3 text-muted-foreground" />
                {programmes.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setProgrammeFilter(p)}
                    className={`rounded-full border px-2 py-0.5 text-[11px] font-medium transition ${
                      programmeFilter === p
                        ? "border-emerald-500 bg-emerald-500 text-white"
                        : "border-border/60 text-muted-foreground hover:border-emerald-300"
                    }`}
                    data-testid={`simu-prog-${p}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>
          {filteredApartments.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
              Aucun appartement dans le catalogue.{" "}
              <span className="text-foreground">
                Configure-les depuis Paramètres → Simulation.
              </span>
            </div>
          ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {filteredApartments.map((a) => {
              const active = apartId === a.id;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => pickApart(a.id)}
                  className={`flex items-center justify-between gap-3 rounded-xl border-2 p-3 text-left transition-all ${
                    active
                      ? "border-emerald-500 bg-emerald-50 shadow-sm dark:bg-emerald-950/30"
                      : "border-border/60 bg-card hover:border-emerald-300"
                  }`}
                  data-testid={`simu-apart-${a.id}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="rounded-md bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 dark:text-emerald-300">
                        {a.typo}
                      </span>
                      <span className="font-heading text-sm font-bold">
                        {a.unite}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {a.surface} m² · {a.programme}
                    </p>
                  </div>
                  <span className="font-heading text-sm font-bold text-emerald-700 dark:text-emerald-300">
                    {fmtFCFA(a.prix)}
                  </span>
                </button>
              );
            })}
          </div>
          )}
        </section>

        {/* 2) Réglages fins — masqués dans un panneau repliable pour rester clean */}
        <details className="mt-4 rounded-xl border border-border/60 bg-muted/20 p-2">
          <summary className="cursor-pointer select-none px-2 py-1 text-[11px] font-medium text-muted-foreground hover:text-foreground">
            Ajuster manuellement (libellé / surface / prix)
          </summary>
          <div className="mt-2 grid gap-3 p-2 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="apart" className="text-[11px]">Libellé</Label>
              <Input
                id="apart"
                value={apartLabel}
                onChange={(e) => setApartLabel(e.target.value)}
                data-testid="simu-apart"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="surface" className="text-[11px]">Surface (m²)</Label>
              <Input
                id="surface"
                type="number"
                value={surface}
                onChange={(e) => setSurface(Number(e.target.value) || 0)}
                data-testid="simu-surface"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="prix" className="text-[11px]">Prix (FCFA)</Label>
              <Input
                id="prix"
                type="number"
                value={prix}
                onChange={(e) => setPrix(Number(e.target.value) || 0)}
                data-testid="simu-prix"
              />
            </div>
          </div>
        </details>

        {/* 2) Mode de financement — généré depuis simulation_modes (admin-éditable) */}
        <section className="mt-6">
          <p className="text-label mb-3 text-muted-foreground">
            2 · Mode de financement
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {modes.map((m) => {
              const active = modeKey === m.key;
              const globallyDisabled = m.enabled === false;
              // Per-apartment block: e.g. F2 doesn't accept Location-Vente.
              const blockedByApart =
                (selectedApart?.disabled_modes || []).includes(m.key);
              const disabled = globallyDisabled || blockedByApart;
              const Icon = m.key === "comptant" ? Wallet : Home;
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => !disabled && setModeKey(m.key)}
                  disabled={disabled}
                  className={`group flex items-start gap-3 rounded-2xl border-2 p-4 text-left transition-all ${
                    disabled
                      ? "cursor-not-allowed border-border/40 bg-muted/30 opacity-60"
                      : active
                        ? "border-emerald-500 bg-emerald-50 shadow-md dark:bg-emerald-950/30"
                        : "border-border/60 bg-card hover:border-emerald-300"
                  }`}
                  data-testid={`mode-${m.key}`}
                  title={
                    blockedByApart
                      ? `Non disponible pour cet appartement`
                      : globallyDisabled
                        ? `Désactivé dans la configuration`
                        : undefined
                  }
                >
                  <div
                    className={`flex h-10 w-10 flex-none items-center justify-center rounded-xl ${
                      active
                        ? "bg-emerald-500 text-white"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-heading font-bold">{m.label}</h3>
                    <p className="text-xs text-muted-foreground">
                      {blockedByApart
                        ? `Non disponible pour ${selectedApart?.typo || "cet appartement"}`
                        : globallyDisabled
                          ? `Désactivé dans la configuration`
                          : m.kind === "monthly"
                            ? `Mensuel · taux ${m.rate_pct}% · apport ${m.downpayment_pct}%`
                            : m.rate_pct
                              ? `Taux ${m.rate_pct}% sur ${m.schedule?.length || 0} échéances`
                              : `${m.schedule?.length || 0} échéances · sans intérêts`}
                    </p>
                  </div>
                  {active && (
                    <Check className="h-5 w-5 flex-none text-emerald-600" />
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* 2bis) Sélecteur de durée + APPORT — visible UNIQUEMENT pour les
            modes "monthly" (location-vente). Le client peut payer plus que
            l'apport minimum configuré (politique SOGI : 30%). Le calcul
            mensuel s'ajuste automatiquement. */}
        {selectedMode?.kind === "monthly" &&
          (selectedMode.durations_years || []).length > 0 && (
            <section className="mt-6">
              <p className="text-label mb-3 text-muted-foreground">
                3 · Durée du remboursement
              </p>
              <div className="flex flex-wrap gap-2">
                {(selectedMode.durations_years || []).map((y) => {
                  const active = durationYears === y;
                  return (
                    <button
                      key={y}
                      type="button"
                      onClick={() => setDurationYears(y)}
                      className={`rounded-xl border-2 px-4 py-2 text-sm font-bold transition-all ${
                        active
                          ? "border-emerald-500 bg-emerald-500 text-white shadow-md"
                          : "border-border/60 bg-card hover:border-emerald-300"
                      }`}
                      data-testid={`simu-duration-${y}`}
                    >
                      {y} an{y > 1 ? "s" : ""}
                      <span className="ml-1.5 text-[11px] font-normal opacity-80">
                        ({y * 12} mois)
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Custom apport row — toggle between % and FCFA, slider on top.
                  Defaults to the configured minimum (e.g. 30%); the user can
                  only INCREASE it. Going above lowers the monthly payment. */}
              <div className="mt-4 rounded-xl border-2 border-emerald-200 bg-emerald-50/40 p-3 dark:border-emerald-900/40 dark:bg-emerald-950/20">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <Label className="text-xs">
                    Apport personnalisé
                    <span className="ml-1 text-[10px] font-normal text-muted-foreground">
                      (min {minDownpayment}% · {fmtFCFA(minDownpaymentAmount)})
                    </span>
                  </Label>
                  {/* Mode toggle: % vs FCFA. The user picks how they want to
                      enter the apport — by percentage or by fixed amount. */}
                  <div className="inline-flex overflow-hidden rounded-md border border-emerald-300/70 bg-white text-[11px] dark:bg-emerald-950/40">
                    {/* Specific transitions only (no `transition: all`).
                        Scale-on-press at 0.96 — the rule of thumb floor. */}
                    <button
                      type="button"
                      onClick={() => setApportMode("percent")}
                      className={`px-2.5 py-1 font-semibold transition-[background-color,color,transform] duration-150 active:scale-[0.96] ${
                        apportMode === "percent"
                          ? "bg-emerald-600 text-white"
                          : "text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300"
                      }`}
                      data-testid="simu-apport-mode-percent"
                    >
                      %
                    </button>
                    <button
                      type="button"
                      onClick={() => setApportMode("amount")}
                      className={`px-2.5 py-1 font-semibold transition-[background-color,color,transform] duration-150 active:scale-[0.96] ${
                        apportMode === "amount"
                          ? "bg-emerald-600 text-white"
                          : "text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300"
                      }`}
                      data-testid="simu-apport-mode-amount"
                    >
                      FCFA
                    </button>
                  </div>
                </div>

                {/* Active input: % or amount, depending on the toggle. */}
                {apportMode === "percent" ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={minDownpayment}
                      max={100}
                      step="1"
                      value={customDownpayment}
                      onChange={(e) =>
                        setCustomDownpayment(Number(e.target.value) || 0)
                      }
                      className="h-9 w-24 text-right text-sm tabular-nums"
                      data-testid="simu-custom-downpayment"
                    />
                    <span className="text-sm font-bold">%</span>
                    <span className="ml-auto text-[11px] text-muted-foreground">
                      ≈{" "}
                      <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                        {fmtFCFA(effectiveDownpaymentAmount)}
                      </span>
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={minDownpaymentAmount}
                      max={prix}
                      step="50000"
                      value={customDownpaymentAmount}
                      onChange={(e) =>
                        setCustomDownpaymentAmount(Number(e.target.value) || 0)
                      }
                      className="h-9 w-40 text-right text-sm tabular-nums"
                      data-testid="simu-custom-downpayment-amount"
                    />
                    <span className="text-sm font-bold">FCFA</span>
                    <span className="ml-auto text-[11px] text-muted-foreground">
                      ≈{" "}
                      <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                        {effectiveDownpayment.toFixed(1)}%
                      </span>
                    </span>
                  </div>
                )}

                {/* Slider — always edits the % (clamped). Useful for both modes. */}
                <input
                  type="range"
                  min={minDownpayment}
                  max={100}
                  step="1"
                  value={effectiveDownpayment}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setApportMode("percent");
                    setCustomDownpayment(v);
                    setCustomDownpaymentAmount(Math.round((prix * v) / 100));
                  }}
                  className="mt-3 w-full accent-emerald-600"
                  aria-label="Apport personnalisé"
                />
                <div className="mt-1.5 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>{minDownpayment}% (min)</span>
                  <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                    {fmtFCFA(effectiveDownpaymentAmount)} d'apport
                  </span>
                  <span>100%</span>
                </div>

                {/* Below-minimum warning. Shown for whichever input is active. */}
                {((apportMode === "percent" && customDownpayment < minDownpayment) ||
                  (apportMode === "amount" &&
                    customDownpaymentAmount < minDownpaymentAmount)) && (
                  <p className="mt-1.5 text-[11px] font-medium text-red-600">
                    L'apport minimum est de {minDownpayment}% (
                    {fmtFCFA(minDownpaymentAmount)}) — politique SOGI. Valeur
                    ramenée au minimum.
                  </p>
                )}
              </div>

              {selectedMode.rate_pct > 0 && (
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Taux annuel : <b>{selectedMode.rate_pct}%</b>
                </p>
              )}
            </section>
          )}

        {/* Résultat */}
        <section className="mt-6">
          <p className="text-label mb-3 text-muted-foreground">Résultat de la simulation</p>

          {/* Editorial summary card — three columns separated by hairline
              vertical rules instead of a heavy border. The mensualité is
              the HERO when in monthly mode: it dominates the column with
              the display serif and a large optical size. */}
          <div className="mb-5 overflow-hidden rounded-2xl border border-emerald-200/60 bg-card shadow-soft dark:border-emerald-900/40">
            {/* Top eyebrow strip — gold/cream gradient, narrow, sets the
                editorial tone for the card */}
            <div
              className="h-1.5 w-full"
              style={{
                background:
                  "linear-gradient(90deg, hsl(var(--accent-navy)) 0%, hsl(var(--accent-gold)) 50%, hsl(var(--accent-navy)) 100%)",
              }}
            />
            <div className="grid grid-cols-1 divide-y divide-border/60 sm:grid-cols-3 sm:divide-y-0 sm:divide-x">
              {/* Prix */}
              <div className="px-5 py-5 sm:px-6 sm:py-6">
                <p className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
                  Prix de vente
                </p>
                <p className="font-display mt-2 text-2xl font-medium tabular-nums text-foreground sm:text-[28px]">
                  {fmtFCFA(prix)}
                </p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70">
                  Comptant
                </p>
              </div>
              {/* Surface */}
              <div className="px-5 py-5 sm:px-6 sm:py-6 sm:text-center">
                <p className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
                  Surface habitable
                </p>
                <p className="font-display mt-2 text-2xl font-medium tabular-nums text-foreground sm:text-[28px]">
                  {surface}
                  <span className="ml-1 text-base font-normal text-muted-foreground">m²</span>
                </p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70">
                  ≈ {prix && surface ? Math.round(prix / surface / 1000) : 0} K FCFA / m²
                </p>
              </div>
              {/* Mensualité ou Mode */}
              {financing ? (
                <div
                  className="relative px-5 py-5 sm:px-6 sm:py-6 sm:text-right"
                  data-testid="simu-mensualite-headline"
                >
                  {/* Subtle gold dot in the top-right to flag this as the
                      hero metric without using a loud background */}
                  <span
                    aria-hidden
                    className="absolute right-5 top-5 h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent-gold))]"
                  />
                  <p className="text-[9px] uppercase tracking-[0.22em] text-emerald-700 dark:text-emerald-300">
                    Mensualité
                  </p>
                  <p className="font-display mt-2 text-3xl font-medium leading-none tabular-nums text-emerald-700 dark:text-emerald-300 sm:text-[36px]">
                    {fmtFCFA(financing.monthly)}
                  </p>
                  <p className="mt-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70">
                    × {financing.months} mois
                  </p>
                </div>
              ) : (
                <div className="px-5 py-5 sm:px-6 sm:py-6 sm:text-right">
                  <p className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
                    Mode de paiement
                  </p>
                  <p className="font-display mt-2 text-2xl font-medium text-foreground sm:text-[28px]">
                    {selectedMode?.label || "—"}
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70">
                    Échéancier complet ci-dessous
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Financing breakdown — editorial layout.
              - Sidebar with rotated label "FINANCEMENT" anchors the panel
                to the gold rule motif from the apartment header
              - Six metrics laid out in a strict 3-column grid with
                hairline dividers (no boxes) so it reads like a magazine
                spread, not a dashboard
              - Top row = inputs (apport / capital / mensualité)
              - Bottom row = outcomes (total payé / intérêts / coût total)
                separated from the inputs by a centered fade hairline */}
          {financing && (
            <div className="mb-5 overflow-hidden rounded-2xl border border-border/60 bg-card shadow-soft">
              <div className="grid grid-cols-[auto_1fr]">
                {/* Vertical label sidebar — gold accent rule + rotated
                    "FINANCEMENT" — sets the editorial tone */}
                <div className="relative flex items-center justify-center border-r border-border/60 bg-[hsl(var(--accent-navy))] px-2 py-6 text-white">
                  <span className="absolute inset-y-0 left-0 w-0.5 bg-[hsl(var(--accent-gold))]" />
                  <span
                    className="font-display whitespace-nowrap text-[10px] font-medium uppercase tracking-[0.4em] text-[hsl(var(--accent-gold))]"
                    style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                  >
                    Financement · {financing.years} an{financing.years > 1 ? "s" : ""}
                  </span>
                </div>

                <div className="p-5 sm:p-6">
                  {/* Inputs row */}
                  <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-3 sm:gap-x-6">
                    <div>
                      <p className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
                        Apport
                      </p>
                      <p className="font-display mt-1.5 text-xl font-medium tabular-nums text-emerald-700 dark:text-emerald-300 sm:text-2xl">
                        {fmtFCFA(financing.apport)}
                      </p>
                      <p className="mt-0.5 text-[10px] tabular-nums text-muted-foreground">
                        {effectiveDownpayment.toFixed(1)}% à la signature
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
                        Capital emprunté
                      </p>
                      <p className="font-display mt-1.5 text-xl font-medium tabular-nums text-foreground sm:text-2xl">
                        {fmtFCFA(financing.capital)}
                      </p>
                      <p className="mt-0.5 text-[10px] tabular-nums text-muted-foreground">
                        au taux de <b className="text-foreground/80">{financing.rate}%</b> / an
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
                        Mensualité
                      </p>
                      <p className="font-display mt-1.5 text-xl font-medium tabular-nums text-emerald-700 dark:text-emerald-300 sm:text-2xl">
                        {fmtFCFA(financing.monthly)}
                      </p>
                      <p className="mt-0.5 text-[10px] tabular-nums text-muted-foreground">
                        × {financing.months} mois consécutifs
                      </p>
                    </div>
                  </div>

                  {/* Hairline separator that fades at both ends */}
                  <div className="divider-hairline my-5" />

                  {/* Outcomes row */}
                  <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-3 sm:gap-x-6">
                    <div>
                      <p className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
                        Total des mensualités
                      </p>
                      <p className="font-display mt-1.5 text-xl font-medium tabular-nums text-foreground sm:text-2xl">
                        {fmtFCFA(financing.totalMensualites)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] uppercase tracking-[0.22em] text-amber-700 dark:text-amber-400">
                        Intérêts payés
                      </p>
                      <p className="font-display mt-1.5 text-xl font-medium tabular-nums text-amber-700 dark:text-amber-400 sm:text-2xl">
                        + {fmtFCFA(financing.interets)}
                      </p>
                      <p className="mt-0.5 text-[10px] tabular-nums text-muted-foreground">
                        coût du crédit
                      </p>
                    </div>
                    <div className="sm:text-right">
                      <p className="text-[9px] uppercase tracking-[0.22em] text-emerald-700 dark:text-emerald-300">
                        ★ Coût total
                      </p>
                      <p className="font-display mt-1.5 text-xl font-medium tabular-nums text-emerald-700 dark:text-emerald-300 sm:text-2xl">
                        {fmtFCFA(financing.coutTotal)}
                      </p>
                      <p className="mt-0.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70">
                        apport + mensualités
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Échéancier */}
          <div className="overflow-hidden rounded-xl border border-border/60 bg-card shadow-soft">
            <div
              className="grid grid-cols-[1.4fr_1fr_0.6fr_1.2fr] items-center gap-2 px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-white"
              style={{ background: "hsl(var(--accent-navy))" }}
            >
              <span>Étape</span>
              <span>Date</span>
              <span className="text-center">%</span>
              <span className="text-right">Montant</span>
            </div>

            {rows.map((r, idx) => (
              <div
                key={r.etape}
                className={`grid grid-cols-[1.4fr_1fr_0.6fr_1.2fr] items-center gap-2 border-t border-border/40 px-4 py-3 text-sm ${
                  idx % 2 === 0 ? "bg-emerald-50/30 dark:bg-emerald-950/10" : "bg-card"
                }`}
                data-testid={`simu-row-${r.etape}`}
              >
                <span className="font-medium">{r.etape}</span>
                <span className="text-muted-foreground">{r.date}</span>
                <span className="text-center">
                  <span
                    className="inline-flex min-w-[44px] items-center justify-center rounded-full bg-emerald-500 px-2 py-0.5 text-[11px] font-bold text-white"
                    style={{
                      backgroundColor: `hsl(160, 70%, ${
                        Math.max(28, 55 - r.pct)
                      }%)`,
                    }}
                  >
                    {r.pct < 1 ? r.pct.toFixed(2) : Math.round(r.pct * 10) / 10}%
                  </span>
                </span>
                <span className="text-right font-semibold tabular-nums">
                  {fmtFCFA(r.montant)}
                  {r.isMonthlySummary && (
                    <span className="block text-[10px] font-normal text-muted-foreground">
                      ({fmtFCFA(r.monthly)} / mois)
                    </span>
                  )}
                </span>
              </div>
            ))}

            {/* Total — barre verte au lieu de l'orange historique */}
            <div
              className="grid grid-cols-[1.4fr_1fr_0.6fr_1.2fr] items-center gap-2 px-4 py-3 text-sm font-bold text-white"
              style={{
                background: "linear-gradient(90deg, hsl(160, 70%, 35%), hsl(160, 70%, 28%))",
              }}
            >
              <span>Total</span>
              <span />
              <span className="text-center">
                {/* In monthly mode each row's pct is computed against the
                    total payments (incl. interests) while the apport row is
                    a % of the price — the sum is meaningless. Hide the
                    chip for those modes; show 100 for schedule modes. */}
                {selectedMode?.kind === "monthly" ? (
                  <span className="text-[11px] text-white/60">—</span>
                ) : (
                  <span className="inline-flex min-w-[44px] items-center justify-center rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-emerald-700">
                    {Math.round(totalPct)}%
                  </span>
                )}
              </span>
              <span className="text-right tabular-nums">{fmtFCFA(totalMontant)}</span>
            </div>
          </div>
        </section>

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          {/* Action row: scale-on-press 0.96 for tactile feedback. The
              transition is property-specific (no `transition: all`). */}
          <Button
            variant="outline"
            onClick={() => navigate("/")}
            className="transition-transform active:scale-[0.96]"
          >
            Annuler
          </Button>
          <Button
            onClick={() => setSendOpen(true)}
            disabled={!selectedApart || rows.length === 0}
            className="gap-1.5 transition-transform active:scale-[0.96] disabled:active:scale-100"
            data-testid="simu-open-send"
          >
            <Send className="h-4 w-4" />
            Envoyer au lead
          </Button>
          <Button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="gap-1.5 bg-emerald-600 transition-[background-color,transform] hover:bg-emerald-700 active:scale-[0.96]"
            title="Revenir en haut pour modifier la simulation"
          >
            <Calculator className="h-4 w-4" />
            Recalculer
          </Button>
        </div>
      </div>

      <SendSimulationDialog
        open={sendOpen}
        onOpenChange={setSendOpen}
        apart={
          selectedApart
            ? {
                ...selectedApart,
                // Override with current edits (the user may have tweaked the
                // labels / surface / prix in the manual editor).
                typo: apartLabel.split("·")[0]?.trim() || selectedApart.typo,
                unite: apartLabel.split("·")[1]?.trim() || selectedApart.unite,
                surface,
                prix,
              }
            : null
        }
        mode={selectedMode}
        durationYears={durationYears}
        effectiveDownpayment={effectiveDownpayment}
        financing={financing}
        rows={rows}
        totalMontant={totalMontant}
      />
    </AppShell>
  );
}
